import { useEffect, useState } from 'react';
import { Cpf, maskCpf } from '@obraplan/core/domain/workforce/cpf';
import type { JobRole } from '@obraplan/core/domain/workforce/team';
import {
  SERVICE_PRICING_LABEL,
  type ServicePricingMode,
} from '@obraplan/core/domain/workforce/execution-resource';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { lerEncargo } from './custo-da-folha';

/**
 * O que o diálogo precisa saber para abrir já preenchido.
 *
 * Tudo em texto: o formulário trabalha com o que foi digitado, e a conversão para número,
 * data ou CPF acontece uma vez só, no envio. Passar `Money` ou `Cpf` para cá obrigaria o
 * diálogo a desmontá-los de volta para editar.
 */
export interface EmployeeDraft {
  readonly name: string;
  readonly jobRoleId: string;
  readonly cpf?: string;
  readonly registration?: string;
  readonly monthlySalary?: string;
  /** O encargo gravado, como o campo o mostra ("80,5"). Ausente = não informado (D12). */
  readonly chargesPercent?: string;
  readonly admittedAt?: string;
  readonly phone?: string;
  readonly notes?: string;
}

export interface TeamDraft {
  readonly name: string;
  readonly leaderEmployeeId?: string;
}

export interface ServiceDraft {
  readonly name: string;
  readonly company?: string;
  readonly contact?: string;
  readonly dailyCapacityHours?: string;
  readonly pricingMode: ServicePricingMode;
  readonly hourlyCost?: string;
  readonly unitOfMeasureId?: string;
  readonly unitCost?: string;
  readonly isActive: boolean;
}

/** Aceita "12,50" e "12.50" — o custo é digitado como se fala. */
function parseMoney(text: string): number | undefined {
  const normalized = text.trim().replace(/\./g, '').replace(',', '.');
  if (normalized === '') return undefined;
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

export interface EmployeeSubmit {
  readonly name: string;
  readonly jobRoleId: string;
  readonly cpf: string | undefined;
  readonly registration: string | undefined;
  readonly monthlySalary: number | undefined;
  /**
   * Os encargos em % (Fase 29, D12). Vai **sempre**, inclusive na edição: o `UpdateEmployee`
   * troca o cadastro inteiro, e o encargo ausente do pedido é o encargo apagado.
   */
  readonly chargesPercent: number | undefined;
  readonly admittedAt: string | undefined;
  readonly phone: string | undefined;
  readonly notes: string | undefined;
}

/**
 * Cadastro de funcionário.
 *
 * O CPF é validado aqui pelo **mesmo** value object que o caso de uso usa (RN-050): não
 * existe validação de formulário paralela à do domínio, que é como as duas divergem com
 * o tempo e uma delas passa a mentir.
 *
 * Salário em branco fica em branco — nunca vira R$ 0,00 (RN-055). E **não existe campo
 * de custo por hora**: ele é calculado a partir do salário, dos encargos e das horas úteis
 * do mês (RN-059, D12). Um campo a menos para preencher é um a menos para ficar
 * desatualizado.
 *
 * O encargo (Fase 29, D12) é um % por funcionário, porque o regime de cada um muda a conta.
 * Em branco é "não informado", e não zero: o custo segue com o salário puro, e a lista avisa.
 */
export function EmployeeDialog({
  open,
  onOpenChange,
  roles,
  employee,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: readonly JobRole[];
  /**
   * Ausente = cadastro novo; presente = edição.
   *
   * O mesmo formulário serve aos dois porque os campos são os mesmos — o que muda é o
   * ponto de partida. Um segundo diálogo quase idêntico seria dois lugares para corrigir
   * a validação do CPF.
   */
  employee?: EmployeeDraft;
  onSubmit: (input: EmployeeSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [jobRoleId, setJobRoleId] = useState('');
  const [cpf, setCpf] = useState('');
  const [registration, setRegistration] = useState('');
  const [cost, setCost] = useState('');
  const [charges, setCharges] = useState('');
  const [admittedAt, setAdmittedAt] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(employee?.name ?? '');
    setJobRoleId(employee?.jobRoleId ?? roles[0]?.id ?? '');
    setCpf(employee?.cpf ?? '');
    setRegistration(employee?.registration ?? '');
    setCost(employee?.monthlySalary ?? '');
    setCharges(employee?.chargesPercent ?? '');
    setAdmittedAt(employee?.admittedAt ?? '');
    setPhone(employee?.phone ?? '');
    setNotes(employee?.notes ?? '');
  }, [open, roles, employee]);

  const isEditing = employee !== undefined;

  const cpfInvalid = cpf.trim() !== '' && !Cpf.isValid(cpf);
  const encargo = lerEncargo(charges);
  const canSubmit = name.trim() !== '' && jobRoleId !== '' && !cpfInvalid && encargo.ok;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar funcionário' : 'Novo funcionário'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Trocar de função, corrigir o salário ou o CPF fica registrado na auditoria (RN-058).'
              : 'O cadastro é desta obra: a mesma pessoa pode estar em dois canteiros sem conflito.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!encargo.ok) return;
            onSubmit({
              name,
              jobRoleId,
              cpf: cpf.trim() === '' ? undefined : cpf.replace(/\D/g, ''),
              registration: registration.trim() === '' ? undefined : registration.trim(),
              monthlySalary: parseMoney(cost),
              chargesPercent: encargo.valor,
              admittedAt: admittedAt === '' ? undefined : admittedAt,
              phone: phone.trim() === '' ? undefined : phone.trim(),
              notes: notes.trim() === '' ? undefined : notes.trim(),
            });
          }}
        >
          <Field label="Nome" htmlFor="employee-name" required error={errorMessage}>
            <Input
              id="employee-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Função"
              htmlFor="employee-role"
              required
              // Sem função não há cadastro possível, e o vazio sozinho não dizia o
              // caminho: quem chegava aqui primeiro ficava com um formulário que não
              // podia ser enviado e nenhuma pista de por onde sair.
              {...(roles.length === 0
                ? { hint: 'Cadastre uma função primeiro, na aba "Funções e disciplinas".' }
                : {})}
            >
              <select
                id="employee-role"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                value={jobRoleId}
                onChange={(event) => setJobRoleId(event.target.value)}
              >
                {roles.length === 0 && <option value="">Nenhuma função cadastrada</option>}
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="CPF"
              htmlFor="employee-cpf"
              error={cpfInvalid ? 'CPF inválido — confira os dígitos.' : undefined}
              hint="Opcional."
            >
              <Input
                id="employee-cpf"
                inputMode="numeric"
                value={cpf}
                onChange={(event) => setCpf(maskCpf(event.target.value))}
                placeholder="000.000.000-00"
                aria-invalid={cpfInvalid}
              />
            </Field>
          </div>

          {/* O salário e o encargo lado a lado: são as duas metades do custo da hora (D12). */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Salário mensal"
              htmlFor="employee-cost"
              hint="O custo por hora é calculado pelo sistema. Em branco fica indisponível — não vira zero."
            >
              <Input
                id="employee-cost"
                inputMode="decimal"
                value={cost}
                onChange={(event) => setCost(event.target.value)}
                placeholder="2.640,00"
              />
            </Field>

            <Field
              label="Encargos (%)"
              htmlFor="employee-charges"
              error={encargo.ok ? undefined : encargo.erro}
              hint="Opcional. INSS, FGTS, férias e 13º sobre o salário — entram no custo da hora."
            >
              <Input
                id="employee-charges"
                inputMode="decimal"
                value={charges}
                onChange={(event) => setCharges(event.target.value)}
                placeholder="80"
                aria-invalid={!encargo.ok}
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Matrícula" htmlFor="employee-registration" hint="Única na obra.">
              <Input
                id="employee-registration"
                value={registration}
                onChange={(event) => setRegistration(event.target.value)}
              />
            </Field>
            <Field label="Admissão" htmlFor="employee-admitted">
              <Input
                id="employee-admitted"
                type="date"
                value={admittedAt}
                onChange={(event) => setAdmittedAt(event.target.value)}
              />
            </Field>
          </div>

          <Field label="Telefone" htmlFor="employee-phone">
            <Input
              id="employee-phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>

          <Field label="Observação" htmlFor="employee-notes">
            <Textarea
              id="employee-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || !canSubmit}>
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TeamDialog({
  open,
  onOpenChange,
  team,
  members,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = equipe nova; presente = edição. */
  team?: TeamDraft;
  /** Quem pode ser líder — os integrantes da própria equipe, na edição. */
  members?: readonly { id: string; name: string }[];
  onSubmit: (input: { name: string; leaderEmployeeId: string | null }) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [leaderId, setLeaderId] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? '');
    setLeaderId(team?.leaderEmployeeId ?? '');
  }, [open, team]);

  const isEditing = team !== undefined;
  const candidates = members ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar equipe' : 'Nova equipe'}</DialogTitle>
          <DialogDescription>
            A equipe é como a obra fala. As horas continuam sendo de cada pessoa.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({ name, leaderEmployeeId: leaderId === '' ? null : leaderId });
          }}
        >
          <Field label="Nome da equipe" htmlFor="team-name" required error={errorMessage}>
            <Input
              id="team-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Alvenaria 01"
            />
          </Field>

          {/*
            O líder sai de quem já está na equipe: apontar alguém de fora produziria uma
            equipe liderada por quem não é dela. Equipe sem líder declarado é estado
            legítimo — por isso a opção vazia continua ali.
          */}
          {isEditing && (
            <Field
              label="Líder"
              htmlFor="team-leader"
              hint={
                candidates.length === 0
                  ? 'Sem integrantes ainda. Atribua a equipe na aba Funcionários.'
                  : 'Opcional. Escolhido entre os integrantes da equipe.'
              }
            >
              <select
                id="team-leader"
                className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9"
                value={leaderId}
                onChange={(event) => setLeaderId(event.target.value)}
                disabled={candidates.length === 0}
              >
                <option value="">— sem líder —</option>
                {candidates.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar equipe'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Nova função — pedreiro, servente, eletricista.
 *
 * Faltava, e a falta trancava a porta de entrada inteira da mão de obra: a função é
 * **obrigatória** no cadastro do funcionário, e o único jeito de uma existir era importar
 * a planilha de funcionários, que as cria pelo rótulo da coluna. Quem não tinha planilha
 * ficava com um formulário que não podia ser enviado e nenhuma tela que resolvesse — o
 * caso de uso existia, o endpoint existia, e ninguém os chamava.
 *
 * O código é opcional de propósito: obra que não numera função não deve ser obrigada a
 * inventar um número para poder cadastrar a primeira.
 */
export function JobRoleDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { name: string; code: string | undefined }) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setCode('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova função</DialogTitle>
          <DialogDescription>
            O que a pessoa faz na obra. É por ela que o sistema sabe a quem propor a alvenaria do 3º
            — e é ela que o cadastro de funcionário exige.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({ name, code: code.trim() === '' ? undefined : code.trim() });
          }}
        >
          <Field label="Nome da função" htmlFor="role-name" required error={errorMessage}>
            <Input
              id="role-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Pedreiro"
            />
          </Field>

          <Field label="Código" htmlFor="role-code" hint="Opcional — o código da folha, se houver.">
            <Input id="role-code" value={code} onChange={(event) => setCode(event.target.value)} />
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
              {isPending ? 'Salvando…' : 'Criar função'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface ServiceSubmit {
  readonly name: string;
  readonly company: string | undefined;
  readonly contact: string | undefined;
  readonly dailyCapacityHours: number | undefined;
  readonly pricingMode: ServicePricingMode;
  readonly hourlyCost: number | undefined;
  readonly unitOfMeasureId: string | undefined;
  readonly unitCost: number | undefined;
  readonly isActive: boolean;
}

/**
 * Novo serviço terceirizado.
 *
 * A decisão que estrutura o formulário é **como a empreiteira cobra**. A de alvenaria
 * costuma faturar por m² levantado, a de impermeabilização por m² aplicado — o que a obra
 * contrata é produção, e as horas da equipe são problema de quem a contratou. Guardar um
 * "custo/hora" para quem cobra por m² obrigaria alguém a inventar um divisor, e o número
 * inventado atravessaria o painel inteiro parecendo medido.
 *
 * Por isso o preço só aparece depois da escolha: cada modo mostra o seu campo, e o do
 * outro não fica gravado (RN-055).
 */
export function ServiceDialog({
  open,
  onOpenChange,
  units,
  service,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Catálogo de unidades — a unidade contratada sai daqui, não de texto livre. */
  units: readonly { id: string; name: string }[];
  /** Ausente = cadastro novo; presente = edição. */
  service?: ServiceDraft;
  onSubmit: (input: ServiceSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');
  const [capacity, setCapacity] = useState('');
  const [pricingMode, setPricingMode] = useState<ServicePricingMode>('HOURLY');
  const [cost, setCost] = useState('');
  const [unitId, setUnitId] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setName(service?.name ?? '');
    setCompany(service?.company ?? '');
    setContact(service?.contact ?? '');
    setCapacity(service?.dailyCapacityHours ?? '');
    setPricingMode(service?.pricingMode ?? 'HOURLY');
    setCost(service?.hourlyCost ?? '');
    setUnitId(service?.unitOfMeasureId ?? '');
    setUnitCost(service?.unitCost ?? '');
    setIsActive(service?.isActive ?? true);
  }, [open, service]);

  const isEditing = service !== undefined;
  const byUnit = pricingMode === 'UNIT';
  const unitName = units.find((unit) => unit.id === unitId)?.name;
  const incomplete = byUnit && unitId === '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar serviço terceirizado' : 'Novo serviço terceirizado'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Trocar a forma de contratação é permitido: o preço do modo abandonado é descartado, não guardado.'
              : 'É recurso de execução, não material: entra no planejamento, não na compra.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              name,
              company: company.trim() === '' ? undefined : company.trim(),
              contact: contact.trim() === '' ? undefined : contact.trim(),
              dailyCapacityHours: parseMoney(capacity),
              pricingMode,
              hourlyCost: byUnit ? undefined : parseMoney(cost),
              unitOfMeasureId: byUnit && unitId !== '' ? unitId : undefined,
              unitCost: byUnit ? parseMoney(unitCost) : undefined,
              isActive,
            });
          }}
        >
          <Field label="Nome do serviço" htmlFor="service-name" required error={errorMessage}>
            <Input
              id="service-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Empreiteira de alvenaria"
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Empresa" htmlFor="service-company">
              <Input
                id="service-company"
                value={company}
                onChange={(event) => setCompany(event.target.value)}
              />
            </Field>
            <Field label="Contato" htmlFor="service-contact">
              <Input
                id="service-contact"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
              />
            </Field>
          </div>

          <Field
            label="Capacidade diária (horas)"
            htmlFor="service-capacity"
            hint="Em branco = sem limite. Uma equipe de doze pessoas não tem 9h por dia."
          >
            <Input
              id="service-capacity"
              inputMode="decimal"
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
              placeholder="—"
            />
          </Field>

          <Field
            label="Forma de contratação"
            htmlFor="service-pricing"
            hint={
              byUnit
                ? 'O custo sai da quantidade medida na conferência, não das horas.'
                : 'O custo sai das horas alocadas para o serviço.'
            }
          >
            <select
              id="service-pricing"
              className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9"
              value={pricingMode}
              onChange={(event) => setPricingMode(event.target.value as ServicePricingMode)}
            >
              {(Object.keys(SERVICE_PRICING_LABEL) as ServicePricingMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {SERVICE_PRICING_LABEL[mode]}
                </option>
              ))}
            </select>
          </Field>

          {byUnit ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Unidade contratada" htmlFor="service-unit" required>
                <select
                  id="service-unit"
                  className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9"
                  value={unitId}
                  onChange={(event) => setUnitId(event.target.value)}
                >
                  <option value="">Escolha a unidade…</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={unitName === undefined ? 'Preço por unidade' : `Preço por ${unitName}`}
                htmlFor="service-unit-cost"
                hint="Em branco = preço indisponível, que não é o mesmo que preço zero."
              >
                <Input
                  id="service-unit-cost"
                  inputMode="decimal"
                  value={unitCost}
                  onChange={(event) => setUnitCost(event.target.value)}
                  placeholder="—"
                />
              </Field>
            </div>
          ) : (
            <Field
              label="Custo por hora"
              htmlFor="service-cost"
              hint="Em branco = preço indisponível, que não é o mesmo que preço zero."
            >
              <Input
                id="service-cost"
                inputMode="decimal"
                value={cost}
                onChange={(event) => setCost(event.target.value)}
                placeholder="—"
              />
            </Field>
          )}

          {/*
            Desativar não é excluir: o serviço some da lista de quem pode receber nova
            alocação, e as alocações que ele já tem continuam onde estão (RN-086).
          */}
          {isEditing && (
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
              Ativo — recebe novas alocações
            </label>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || name.trim() === '' || incomplete}
            >
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Cadastrar serviço'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
